import React, { createContext, useContext, useCallback, useState } from "react";
import { Home as HomeIcon } from "@mui/icons-material"; // ⬅ import once

export interface FooterButton {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
  variant?: "circular" | "square";
}

export interface FooterConfig {
  left?: FooterButton; // optional → if undefined, button is hidden
  middle?: FooterButton;
  right?: FooterButton;
}

type FooterContextType = {
  config: FooterConfig;
  setConfig: (cfg: FooterConfig) => void;
};

/* ---------- DEFAULT STATE WHEN NO PAGE CALLS useFooter() --------------- */
const defaultConfig: FooterConfig = {
  middle: {
    label: "Home",
    icon: <HomeIcon />,
    to: "/wallet",
  },
};

const FooterContext = createContext<FooterContextType>({
  config: defaultConfig,
  setConfig: () => {},
});

export const FooterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfigState] = useState<FooterConfig>(defaultConfig);
  const setConfig = useCallback((cfg: FooterConfig) => setConfigState(cfg), []);

  return (
    <FooterContext.Provider value={{ config, setConfig }}>
      {children}
    </FooterContext.Provider>
  );
};

/* pages call this to override; on unmount we reset to the default above */
export const useFooter = (cfg: FooterConfig): void => {
  const { setConfig } = useContext(FooterContext);
  React.useEffect(() => {
    setConfig(cfg);
    return () => setConfig(defaultConfig);
  }, [cfg, setConfig]);
};

export const useFooterContext = () => useContext(FooterContext);
